import React, { useState, lazy, Suspense, useEffect, useCallback } from 'react';
import { MMECTopBar } from './MMECTopBar';
import { MMECBottomTabBar, type SimpleTab } from './MMECBottomTabBar';
import { ScannerData } from '../../types/ScannerData';
import { useDataSource } from '../../contexts/DataSourceContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useFlowLiquidityAlerts } from '../../hooks/useFlowLiquidityAlerts';
import { useAlertSystem } from '../../hooks/useAlertSystem';
import AlertSettingsModal from '../AlertSettingsModal';
import { ALL_FIELDS } from '../../config/alertFields';
import { ViewLoading } from './MMECLoading';
import { MMEC_SIMPLE_TOUCH_MIN } from '../../theme/mmecSimpleMobile';

// Lazy-load heavy tools so the simple shell stays fast
const OptionsFlow = lazy(() => import('../analysis/OptionsFlow'));
const DataTable = lazy(() => import('../DataTable'));
const RealTimeGexChart = lazy(() => import('../RealTimeGexChart'));
const FlowHeatmap = lazy(() => import('../FlowHeatmap'));
const UnifiedLegacyVolumeTool = lazy(() => import('../UnifiedLegacyVolumeTool'));
const CryptoView = lazy(() => import('../crypto/CryptoView'));
import RtlScannerView from '../RtlScannerView';
import TrackedContractsView from '../TrackedContractsView';
import ContractSummary from '../analytics/ContractSummary';
import type { TrackedContract } from '../../contexts/ContractTrackingContext';

const StableDataFetchBar = lazy(() => import('../StableDataFetchBar'));

interface MMECSimpleShellProps {
  data: ScannerData[];
  dataMode?: 'accumulated' | '5min';
}

function TabSpinner() {
  return <ViewLoading />;
}

export const MMECSimpleShell: React.FC<MMECSimpleShellProps> = ({
  data,
  dataMode = 'accumulated',
}) => {
  const { dataSource } = useDataSource();
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState<SimpleTab>('flow');
  /** GEX tab symbol is independent of Stable fetch bar (avoids SPY overwriting SPX). */
  const [gexSymbol, setGexSymbol] = useState('SPX');
  const [flowAlertsOpen, setFlowAlertsOpen] = useState(false);
  const [analyzeContract, setAnalyzeContract] = useState<TrackedContract | null>(null);

  const {
    alertSettings,
    handleAlertSettingsChange,
    loadAlertSettings,
  } = useAlertSystem();

  useFlowLiquidityAlerts(true);

  useEffect(() => {
    loadAlertSettings();
  }, [loadAlertSettings]);

  const openFlowAlerts = useCallback(() => setFlowAlertsOpen(true), []);

  // Floating controls outside the shell (fetch log, launchers, badges) read
  // this to sit above the bottom tab bar instead of on top of it.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--mmec-bottom-offset', `${MMEC_SIMPLE_TOUCH_MIN + 8}px`);
    return () => {
      root.style.removeProperty('--mmec-bottom-offset');
    };
  }, []);

  useEffect(() => {
    const onNav = (e: Event) => {
      const t = (e as CustomEvent<{ tab?: SimpleTab }>).detail?.tab;
      if (t === 'elite_pulse' || t === 'flow' || t === 'scanner' || t === 'gex' || t === 'volume' || t === 'analyze' || t === 'crypto') {
        setActiveTab(t);
      }
    };
    window.addEventListener('mmec-simple-navigate-tab', onNav as EventListener);
    return () => window.removeEventListener('mmec-simple-navigate-tab', onNav as EventListener);
  }, []);

  function renderTab() {
    switch (activeTab) {
      case 'flow':
        return (
          <Suspense fallback={<TabSpinner />}>
            <OptionsFlow simpleLayout />
          </Suspense>
        );
      case 'scanner':
        return (
          <Suspense fallback={<TabSpinner />}>
            <DataTable data={data} simpleLayout />
          </Suspense>
        );
      case 'analyze':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            <div style={{ flex: analyzeContract ? '0 0 42%' : '1 1 auto', overflow: 'auto', minHeight: 0 }}>
              <TrackedContractsView
                simpleLayout
                onSelectContract={(c) => setAnalyzeContract(c)}
              />
            </div>
            {analyzeContract ? (
              <div style={{ flex: '1 1 58%', overflow: 'auto', borderTop: '1px solid var(--hair)', minHeight: 0 }}>
                <ContractSummary
                  contract={{
                    symbol: analyzeContract.symbol,
                    strike: analyzeContract.strike,
                    expiry: analyzeContract.expiry,
                    optionType: analyzeContract.optionType,
                    id: analyzeContract.id,
                  }}
                  tracked={analyzeContract}
                  simpleLayout
                  embedded
                />
              </div>
            ) : (
              <div style={{ padding: 20, textAlign: 'center', color: 'var(--dim)', fontSize: 13, borderTop: '1px solid var(--hair)' }}>
                {language === 'ar'
                  ? 'اضغط ☆ على أي عقد، ثم اضغط صفاً هنا'
                  : 'Tap ☆ on any contract, then tap a row here'}
              </div>
            )}
          </div>
        );
      case 'gex':
        return (
          <Suspense fallback={<TabSpinner />}>
            <RealTimeGexChart
              symbol={gexSymbol}
              simpleLayout
              onSymbolChange={setGexSymbol}
            />
          </Suspense>
        );
      case 'volume':
        return (
          <Suspense fallback={<TabSpinner />}>
            {dataSource === 'rtl_legacy' ? (
              <UnifiedLegacyVolumeTool data={data} dataMode={dataMode} simpleLayout />
            ) : (
              <FlowHeatmap data={data} dataMode={dataMode} simpleLayout />
            )}
          </Suspense>
        );
      case 'elite_pulse':
        return (
          <Suspense fallback={<TabSpinner />}>
            <RtlScannerView simpleLayout />
          </Suspense>
        );
      case 'crypto':
        return (
          <Suspense fallback={<TabSpinner />}>
            <CryptoView />
          </Suspense>
        );
      default:
        return null;
    }
  }

  return (
    <>
      <style>{`
        .mmec-simple-shell {
          display: flex;
          flex-direction: column;
          height: 100vh;
          width: 100vw;
          background: var(--base);
          color: var(--text);
          font-family: var(--font-ui, 'IBM Plex Sans', sans-serif);
          overflow: hidden;
        }

        /* On desktop, constrain to a mobile-like centred column */
        @media (min-width: 768px) {
          .mmec-simple-shell {
            align-items: center;
            background: var(--void);
          }
          .mmec-simple-inner {
            width: 100%;
            max-width: 480px;
            height: 100%;
            display: flex;
            flex-direction: column;
            background: var(--base);
            box-shadow: 0 0 40px rgba(0,0,0,0.4);
          }
        }

        @media (max-width: 767px) {
          .mmec-simple-inner {
            display: flex;
            flex-direction: column;
            height: 100%;
            width: 100%;
          }
        }

        .mmec-simple-canvas {
          flex: 1;
          overflow: auto;
          position: relative;
          background: var(--base);
        }

        .mmec-simple-canvas.mmec-simple-canvas-fill {
          display: flex;
          flex-direction: column;
          min-height: 0;
          /* Allow nested overflow-x-auto to receive a bounded width (flex default min-width:auto clips wide tables). */
          min-width: 0;
          overflow: hidden;
        }

        .mmec-simple-stable-strip {
          flex-shrink: 0;
          border-bottom: 1px solid var(--hair);
          background: var(--elev);
        }

        .mmec-simple-canvas::-webkit-scrollbar {
          width: 4px;
        }
        .mmec-simple-canvas::-webkit-scrollbar-thumb {
          background: var(--hair);
          border-radius: 2px;
        }
        .mmec-simple-canvas::-webkit-scrollbar-track {
          background: transparent;
        }

      `}</style>

      <div className="mmec-simple-shell">
        <div className="mmec-simple-inner" dir={language === 'ar' ? 'rtl' : 'ltr'}>
          <MMECTopBar simpleMode onOpenFlowAlerts={openFlowAlerts} />

          {dataSource === 'stable' && (
            <div className="mmec-simple-stable-strip" data-tour="mmec-stable-fetch">
              <Suspense fallback={null}>
                <StableDataFetchBar compactLayout />
              </Suspense>
            </div>
          )}

          <main
            className={`mmec-simple-canvas${
              activeTab === 'volume' || activeTab === 'gex' || activeTab === 'scanner' || activeTab === 'flow' || activeTab === 'elite_pulse' || activeTab === 'crypto'
                ? ' mmec-simple-canvas-fill'
                : ''
            }`}
          >
            {renderTab()}
          </main>

          <MMECBottomTabBar
            activeTab={activeTab}
            onTabChange={setActiveTab}
            language={language}
          />
        </div>
      </div>

      {flowAlertsOpen && (
        <AlertSettingsModal
          isOpen={flowAlertsOpen}
          onClose={() => setFlowAlertsOpen(false)}
          alertSettings={alertSettings}
          onSave={handleAlertSettingsChange}
          availableFields={ALL_FIELDS}
        />
      )}
    </>
  );
};

export default MMECSimpleShell;
